export const DROPDOWN_MIN_WIDTH = 600;
export const DROPDOWN_MIN_HEIGHT = 330;

export const getViewportRect = () => ({
  bottom: typeof window !== 'undefined' ? window.innerHeight : Infinity,
  height: typeof window !== 'undefined' ? window.innerHeight : Infinity,
  left: 0,
  right: typeof window !== 'undefined' ? window.innerWidth : Infinity,
  top: 0,
  width: typeof window !== 'undefined' ? window.innerWidth : Infinity,
});

export interface AnchorRect {
  left: number;
  right: number;
}

export interface DropdownLayout {
  forceMaxWidth: boolean;
  maxWidth: number;
  minWidth: number | '100%';
  minHeight: number;
  positionLeft?: number;
  positionRight?: number;
}

/** Computes dropdown layout (positioning and dimensions) */
export const computeDropdownLayout = (
  anchorRect: AnchorRect,
  flightMode: boolean,
  anchorWidth: number,
): DropdownLayout => {
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : Infinity;
  const widgetLeft = anchorRect.left;
  const widgetRight = anchorRect.right;
  const spaceToRightOfScreen = windowWidth - widgetRight;
  const isWideWidget = anchorWidth >= DROPDOWN_MIN_WIDTH;

  let positionLeft: number | undefined;
  let positionRight: number | undefined;

  if (isWideWidget) {
    positionRight = spaceToRightOfScreen;
    positionLeft = widgetLeft;
  } else {
    const notEnoughSpaceToRight = windowWidth - widgetLeft < DROPDOWN_MIN_WIDTH;
    const anchorToViewportRight = flightMode ? widgetRight >= DROPDOWN_MIN_WIDTH : notEnoughSpaceToRight;
    if (anchorToViewportRight) {
      positionRight = spaceToRightOfScreen;
    } else {
      positionLeft = widgetLeft;
    }
  }
  return {
    forceMaxWidth: flightMode,
    maxWidth: isWideWidget ? anchorWidth : DROPDOWN_MIN_WIDTH,
    minHeight: DROPDOWN_MIN_HEIGHT,
    minWidth: isWideWidget ? '100%' : DROPDOWN_MIN_WIDTH,
    positionLeft,
    positionRight,
  };
};

export interface ResolveConstrainedFloaterLayoutInput {
  allowOverflowParent?: boolean;
  anchorRect?: AnchorRect;
  anchorWidth?: number;
  flightMode?: boolean;
  forceMaxWidth?: boolean;
  maxWidth?: number;
  minHeight?: number;
  minWidth?: number | `100%`;
  positionLeft?: number | null;
  positionRight?: number | null;
}

export interface ResolvedConstrainedFloaterLayout {
  forceMaxWidth: boolean | undefined;
  maxWidth: number | undefined;
  minHeight: number | undefined;
  minWidth: number | `100%` | undefined;
  positionLeft: number | null | undefined;
  positionRight: number | null | undefined;
}

/** Merges anchor-based dropdown layout with explicit props when overflow mode is active */
export const resolveConstrainedFloaterLayout = (
  props: ResolveConstrainedFloaterLayoutInput,
): ResolvedConstrainedFloaterLayout => {
  const computedLayout =
    props.allowOverflowParent && props.anchorRect != null && props.anchorWidth != null
      ? computeDropdownLayout(props.anchorRect, props.flightMode ?? false, props.anchorWidth)
      : null;

  return {
    forceMaxWidth: computedLayout?.forceMaxWidth ?? props.forceMaxWidth,
    maxWidth: computedLayout?.maxWidth ?? props.maxWidth,
    minHeight: computedLayout?.minHeight ?? props.minHeight,
    minWidth: computedLayout?.minWidth ?? props.minWidth,
    positionLeft: computedLayout?.positionLeft ?? props.positionLeft,
    positionRight: computedLayout?.positionRight ?? props.positionRight,
  };
};
